import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Download,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  SkipForward,
  X,
} from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getInvoiceDraftCsvUrl,
  listInvoiceDrafts,
  markInvoiceDraftExported,
  skipInvoiceDraft,
  type InvoiceDraft,
} from '@/features/planning/api';
import type { Language } from '@/types';
import { cn } from '@/lib/utils';

interface InvoicingWorkspaceProps {
  language: Language;
}

const copy = {
  en: {
    title: 'Invoicing',
    description: '',
    adminOnly: 'Only admins can view invoicing.',
    prev: 'Previous',
    next: 'Next',
    refresh: 'Refresh',
    noDrafts: 'No drafts for this month.',
    statusPending: 'Pending',
    statusExported: 'Exported',
    statusSkipped: 'Skipped',
    markExported: 'Mark exported',
    skip: 'Skip',
    csv: 'Download CSV',
    fortnoxNumber: 'Fortnox invoice #',
    note: 'Note',
    base: 'Base',
    extras: 'Extras',
    total: 'Total',
    customer: 'Customer',
    missingPrice: 'Price missing',
    expand: 'Show details',
    collapse: 'Hide details',
    actions: 'Actions',
    kpiSites: 'Sites',
    kpiPending: 'Pending',
    kpiExported: 'Exported',
    kpiTotal: 'Total invoiced',
    columnSite: 'Site',
    columnStatus: 'Status',
    columnBase: 'Base',
    columnExtras: 'Extras',
    columnTotal: 'Total',
    skipReason: 'Enter reason for skip',
    lines: 'Lines',
    filterSite: 'BRF',
    filterStatus: 'Status',
    all: 'All',
    searchPlaceholder: 'Search by name or customer #',
    clearFilters: 'Clear',
    showingOf: (x: number, y: number) => `Showing ${x} of ${y}`,
    noMatch: 'No BRFs match the current filter.',
    noCustomer: 'No customer #',
    pendingExtras: (n: number) => `${n} unapproved extra${n === 1 ? '' : 's'}`,
  },
  sv: {
    title: 'Fakturering',
    description: '',
    adminOnly: 'Endast admin kan se fakturering.',
    prev: 'Föregående',
    next: 'Nästa',
    refresh: 'Uppdatera',
    noDrafts: 'Inga utkast för denna månad.',
    statusPending: 'Väntar',
    statusExported: 'Exporterad',
    statusSkipped: 'Överhoppad',
    markExported: 'Markera exporterad',
    skip: 'Hoppa över',
    csv: 'Ladda ner CSV',
    fortnoxNumber: 'Fortnox fakturanr',
    note: 'Anteckning',
    base: 'Bas',
    extras: 'Extra',
    total: 'Totalt',
    customer: 'Kundnr',
    missingPrice: 'Pris saknas',
    expand: 'Visa detaljer',
    collapse: 'Dölj detaljer',
    actions: 'Åtgärder',
    kpiSites: 'Objekt',
    kpiPending: 'Väntar',
    kpiExported: 'Exporterad',
    kpiTotal: 'Summa faktureras',
    columnSite: 'Objekt',
    columnStatus: 'Status',
    columnBase: 'Bas',
    columnExtras: 'Extra',
    columnTotal: 'Totalt',
    skipReason: 'Ange anledning för överhoppning',
    lines: 'Rader',
    filterSite: 'BRF',
    filterStatus: 'Status',
    all: 'Alla',
    searchPlaceholder: 'Sök på namn eller kundnr',
    clearFilters: 'Rensa',
    showingOf: (x: number, y: number) => `Visar ${x} av ${y}`,
    noMatch: 'Inga BRF:er matchar filtret.',
    noCustomer: 'Saknar kundnr',
    pendingExtras: (n: number) => `${n} extra ej godkän${n === 1 ? 'd' : 'da'}`,
  },
} as const;

const MONTH_NAMES_SV = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
const MONTH_NAMES_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function currentYearMonth(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function shift(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function formatSek(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(value);
}

type ChipTone = 'ok' | 'warn' | 'crit' | 'muted';
type InvoicingCopy = typeof copy[keyof typeof copy];

function statusMeta(status: InvoiceDraft['status'], text: InvoicingCopy): { label: string; tone: ChipTone } {
  if (status === 'exported') return { label: text.statusExported, tone: 'ok' };
  if (status === 'skipped') return { label: text.statusSkipped, tone: 'muted' };
  return { label: text.statusPending, tone: 'warn' };
}

const CHIP_STYLE: Record<ChipTone, { bg: string; fg: string }> = {
  ok: { bg: 'rgba(27,127,77,0.10)', fg: '#1B7F4D' },
  warn: { bg: 'rgba(185,126,31,0.12)', fg: '#B97E1F' },
  crit: { bg: 'rgba(194,65,56,0.12)', fg: '#C24138' },
  muted: { bg: 'rgba(7,51,49,0.06)', fg: '#607C6E' },
};

function Chip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  const s = CHIP_STYLE[tone];
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wide whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
    >
      {children}
    </span>
  );
}

function hasPlaceholderPrice(draft: InvoiceDraft): boolean {
  if ((draft.total_inc_moms ?? 0) > 0) return false;
  return draft.lines.some((line) => line.line_type === 'recurring' && (line.price_ex_moms ?? 0) === 0);
}

export function InvoicingWorkspace({ language }: InvoicingWorkspaceProps) {
  const { session, profile } = useAuth();
  const text = copy[language];
  const token = session?.access_token ?? null;
  const isAdmin = profile?.role === 'admin';

  const { year: initY, month: initM } = currentYearMonth();
  const [year, setYear] = useState(initY);
  const [month, setMonth] = useState(initM);
  const [drafts, setDrafts] = useState<InvoiceDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [exportForm, setExportForm] = useState<Record<string, { fortnoxNumber: string; note: string }>>({});
  const [siteFilter, setSiteFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [siteOpen, setSiteOpen] = useState(false);

  const monthLabel = (language === 'sv' ? MONTH_NAMES_SV : MONTH_NAMES_EN)[month - 1];

  const loadDrafts = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await listInvoiceDrafts(year, month, token);
      setDrafts(res);
    } catch (err) {
      console.error('Failed to load drafts', err);
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) void loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, year, month, isAdmin]);

  const draftKey = (d: InvoiceDraft) => `${d.site_id}-${d.period_year}-${d.period_month}`;

  const handleMarkExported = async (draft: InvoiceDraft) => {
    if (!token) return;
    const key = draftKey(draft);
    const form = exportForm[key] ?? { fortnoxNumber: '', note: '' };
    setExportingId(key);
    try {
      await markInvoiceDraftExported(
        {
          siteId: draft.site_id,
          year: draft.period_year,
          month: draft.period_month,
          fortnoxInvoiceNumber: form.fortnoxNumber.trim() || null,
          note: form.note.trim() || null,
        },
        token,
      );
      setExportForm((prev) => ({ ...prev, [key]: { fortnoxNumber: '', note: '' } }));
      setExpandedId(null);
      await loadDrafts();
    } catch (err) {
      console.error('Mark exported failed', err);
    } finally {
      setExportingId(null);
    }
  };

  const handleSkip = async (draft: InvoiceDraft) => {
    if (!token) return;
    const note = window.prompt(text.skipReason);
    if (!note?.trim()) return;
    try {
      await skipInvoiceDraft(
        {
          siteId: draft.site_id,
          year: draft.period_year,
          month: draft.period_month,
          note: note.trim(),
        },
        token,
      );
      await loadDrafts();
    } catch (err) {
      console.error('Skip failed', err);
    }
  };

  const handleCsvDownload = async (draft: InvoiceDraft) => {
    if (!token || !draft.id) return;
    try {
      const res = await fetch(getInvoiceDraftCsvUrl(draft.site_id, year, month), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`csv fetch failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${draft.site_name}-${year}-${String(month).padStart(2, '0')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed', err);
    }
  };

  const kpi = useMemo(() => ({
    sites: drafts.length,
    pending: drafts.filter((d) => d.status === 'pending').length,
    exported: drafts.filter((d) => d.status === 'exported').length,
    total: drafts.reduce((sum, d) => sum + (d.total_inc_moms ?? 0), 0),
  }), [drafts]);

  const filteredDrafts = useMemo(() => {
    return drafts.filter((d) => {
      if (siteFilter !== 'all' && d.site_id !== siteFilter) return false;
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      return true;
    });
  }, [drafts, siteFilter, statusFilter]);

  const filterActive = siteFilter !== 'all' || statusFilter !== 'all';
  const selectedSite = drafts.find((d) => d.site_id === siteFilter);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="cmd-panel cmd-panel-inner rounded p-8 max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-[#607C6E] mx-auto mb-3" />
          <p className="text-sm font-mono text-[#2E4640]">{text.adminOnly}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-lg font-mono font-semibold tracking-tight text-[#073331]">{text.title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center cmd-panel rounded">
            <button
              type="button"
              className="h-9 w-9 flex items-center justify-center text-[#607C6E] hover:text-[#073331] transition-colors"
              onClick={() => { const n = shift(year, month, -1); setYear(n.year); setMonth(n.month); }}
              aria-label={text.prev}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="min-w-[150px] text-center text-sm font-mono font-semibold text-[#073331] px-2">
              {monthLabel} {year}
            </div>
            <button
              type="button"
              className="h-9 w-9 flex items-center justify-center text-[#607C6E] hover:text-[#073331] transition-colors"
              onClick={() => { const n = shift(year, month, 1); setYear(n.year); setMonth(n.month); }}
              aria-label={text.next}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={loadDrafts} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">{text.refresh}</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label={text.kpiSites} value={String(kpi.sites)} />
        <StatTile label={text.kpiPending} value={String(kpi.pending)} tone="warn" />
        <StatTile label={text.kpiExported} value={String(kpi.exported)} tone="ok" />
        <StatTile label={text.kpiTotal} value={formatSek(kpi.total)} tone="strong" />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 -mt-1">
        <div className="flex items-center gap-1.5 min-w-[280px] flex-1 max-w-[360px]">
          <label className="cmd-label shrink-0">{text.filterSite}</label>
          <Popover open={siteOpen} onOpenChange={setSiteOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" aria-expanded={siteOpen} className="h-8 w-full justify-between text-xs font-mono font-normal">
                <span className="truncate">
                  {siteFilter === 'all' ? text.all : (
                    <span className="inline-flex items-center gap-2">
                      <span className="font-medium">{selectedSite?.site_name ?? '—'}</span>
                      <span className="h-4 w-px bg-border" aria-hidden />
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {selectedSite?.fortnox_customer_number ?? text.noCustomer}
                      </span>
                    </span>
                  )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] p-0" align="start">
              <Command
                filter={(value, search) => {
                  if (!search) return 1;
                  return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                }}
              >
                <CommandInput placeholder={text.searchPlaceholder} />
                <CommandList>
                  <CommandEmpty>—</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => { setSiteFilter('all'); setSiteOpen(false); }}>
                      <Check className={cn('mr-2 h-4 w-4', siteFilter === 'all' ? 'opacity-100' : 'opacity-0')} />
                      {text.all}
                    </CommandItem>
                    {drafts.map((d) => {
                      const customer = d.fortnox_customer_number ?? '';
                      const searchValue = `${d.site_name} ${customer}`.trim();
                      return (
                        <CommandItem
                          key={d.site_id}
                          value={searchValue}
                          onSelect={() => { setSiteFilter(d.site_id); setSiteOpen(false); }}
                        >
                          <Check className={cn('mr-2 h-4 w-4', siteFilter === d.site_id ? 'opacity-100' : 'opacity-0')} />
                          <span className="flex-1 truncate">{d.site_name}</span>
                          <span className="mx-2 h-4 w-px bg-border shrink-0" aria-hidden />
                          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                            {customer || text.noCustomer}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-1.5">
          <label className="cmd-label shrink-0">{text.filterStatus}</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-[140px] text-xs font-mono"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{text.all}</SelectItem>
              <SelectItem value="pending">{text.statusPending}</SelectItem>
              <SelectItem value="exported">{text.statusExported}</SelectItem>
              <SelectItem value="skipped">{text.statusSkipped}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filterActive && (
          <div className="flex items-center gap-3 ml-auto">
            <span className="text-xs font-mono text-[#607C6E] tabular-nums">
              {text.showingOf(filteredDrafts.length, drafts.length)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSiteFilter('all'); setStatusFilter('all'); }}
            >
              <X className="h-4 w-4 mr-1" />
              {text.clearFilters}
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#607C6E]" />
        </div>
      ) : drafts.length === 0 ? (
        <div className="cmd-panel cmd-panel-inner rounded py-10 text-center text-sm font-mono text-[#607C6E]">
          {text.noDrafts}
        </div>
      ) : filteredDrafts.length === 0 ? (
        <div className="cmd-panel cmd-panel-inner rounded py-10 text-center text-sm font-mono text-[#607C6E]">
          {text.noMatch}
        </div>
      ) : (
        <div className="cmd-panel cmd-panel-inner rounded overflow-hidden">
          <div className="hidden md:grid grid-cols-[1fr_140px_120px_120px_140px_80px] items-center gap-3 px-4 py-2 border-b border-[#073331]/10">
            <div className="cmd-label">{text.columnSite}</div>
            <div className="cmd-label">{text.columnStatus}</div>
            <div className="cmd-label text-right">{text.columnBase}</div>
            <div className="cmd-label text-right">{text.columnExtras}</div>
            <div className="cmd-label text-right">{text.columnTotal}</div>
            <div className="cmd-label text-right">{text.actions}</div>
          </div>
          <div className="divide-y divide-[#073331]/[0.06]">
            {filteredDrafts.map((draft) => {
              const key = draftKey(draft);
              const expanded = expandedId === key;
              const sm = statusMeta(draft.status, text);
              const placeholder = hasPlaceholderPrice(draft);
              const form = exportForm[key] ?? { fortnoxNumber: '', note: '' };
              return (
                <div key={key} className={cn('group', expanded && 'bg-[#073331]/[0.03]')}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : key)}
                    className="w-full text-left px-4 py-1.5 hover:bg-[#073331]/[0.04] transition-colors"
                  >
                    <div className="grid md:grid-cols-[1fr_140px_120px_120px_140px_80px] gap-3 items-center">
                      <div className="flex items-center gap-2 min-w-0">
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 shrink-0 text-[#607C6E] transition-transform',
                            expanded ? 'rotate-0' : '-rotate-90',
                          )}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="text-sm font-mono font-medium text-[#2E4640] group-hover:text-[#073331] transition-colors truncate">{draft.site_name}</span>
                            <span className="h-3.5 w-px bg-[#073331]/15 shrink-0" aria-hidden />
                            <span className="text-xs font-mono text-[#607C6E] tabular-nums shrink-0">
                              {draft.fortnox_customer_number ?? text.noCustomer}
                            </span>
                          </div>
                          {draft.fortnox_invoice_number && (
                            <div className="text-xs font-mono text-[#607C6E] truncate mt-0.5">
                              #{draft.fortnox_invoice_number}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 flex-nowrap">
                        <Chip tone={sm.tone}>{sm.label}</Chip>
                        {placeholder && (
                          <span title={text.missingPrice} className="inline-flex shrink-0 items-center rounded px-1 py-0.5" style={{ background: 'rgba(194,65,56,0.12)' }}>
                            <AlertTriangle className="h-3 w-3 text-[#C24138]" />
                          </span>
                        )}
                        {draft.pending_extras_count > 0 && (
                          <span title={text.pendingExtras(draft.pending_extras_count)} className="inline-flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 font-mono text-[10px] tabular-nums text-[#B97E1F]" style={{ background: 'rgba(185,126,31,0.12)' }}>
                            <AlertTriangle className="h-3 w-3" />
                            {draft.pending_extras_count}
                          </span>
                        )}
                      </div>

                      <div className="text-right text-sm font-mono tabular-nums text-[#607C6E]">
                        {formatSek(draft.base_amount_ex_moms)}
                      </div>
                      <div className="text-right text-sm font-mono tabular-nums text-[#607C6E]">
                        {formatSek(draft.extras_amount_ex_moms)}
                      </div>
                      <div className="text-right text-sm font-mono font-semibold tabular-nums text-[#073331]">
                        {formatSek(draft.total_inc_moms)}
                      </div>

                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={!draft.id || !token}
                              onSelect={() => handleCsvDownload(draft)}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              {text.csv}
                            </DropdownMenuItem>
                            {draft.status === 'pending' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onSelect={() => handleSkip(draft)}>
                                  <SkipForward className="h-4 w-4 mr-2" />
                                  {text.skip}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-4 pt-1 space-y-4">
                      {draft.admin_note && (
                        <div className="text-xs font-mono text-[#607C6E] italic">{draft.admin_note}</div>
                      )}

                      {draft.lines.length > 0 && (
                        <div className="border border-[#073331]/12 rounded overflow-hidden bg-white">
                          <div className="px-3 py-1.5 cmd-label border-b border-[#073331]/10">
                            {text.lines}
                          </div>
                          <table className="w-full text-sm">
                            <tbody>
                              {draft.lines.map((line) => {
                                const isZero = (line.price_ex_moms ?? 0) === 0 && line.line_type === 'recurring';
                                const isPendingExtra = line.line_type === 'extra' && /ej godkänd/i.test(line.description ?? '');
                                return (
                                  <tr key={line.id} className="border-b border-[#073331]/[0.06] last:border-b-0">
                                    <td className="px-3 py-2">
                                      <div className="font-mono text-[#2E4640] flex items-center gap-2">
                                        {line.description}
                                        {isZero && (
                                          <Chip tone="crit">
                                            <AlertTriangle className="h-3 w-3" />
                                            {text.missingPrice}
                                          </Chip>
                                        )}
                                        {isPendingExtra && (
                                          <Chip tone="warn">
                                            <AlertTriangle className="h-3 w-3" />
                                            {text.pendingExtras(1)}
                                          </Chip>
                                        )}
                                      </div>
                                      <div className="text-xs font-mono text-[#607C6E]">{line.line_type}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap font-mono tabular-nums text-[#073331]">
                                      {formatSek(line.price_ex_moms)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {draft.status === 'pending' && (
                        <div className="grid gap-3 md:grid-cols-[220px_1fr_auto] md:items-end">
                          <div className="space-y-1">
                            <label className="cmd-label block">{text.fortnoxNumber}</label>
                            <Input
                              value={form.fortnoxNumber}
                              onChange={(e) => setExportForm((prev) => ({ ...prev, [key]: { ...form, fortnoxNumber: e.target.value } }))}
                              placeholder="2026-0042"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="cmd-label block">{text.note}</label>
                            <Textarea
                              value={form.note}
                              onChange={(e) => setExportForm((prev) => ({ ...prev, [key]: { ...form, note: e.target.value } }))}
                              rows={1}
                              className="min-h-[40px] resize-none"
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleMarkExported(draft)}
                            disabled={exportingId === key}
                          >
                            {exportingId === key ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4" />
                            )}
                            <span className="ml-1.5">{text.markExported}</span>
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const TONE_NUMERAL: Record<string, string> = {
  ok: '#1B7F4D',
  warn: '#B97E1F',
  strong: '#073331',
  neutral: '#073331',
};

interface StatTileProps {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'strong' | 'neutral';
}

function StatTile({ label, value, tone = 'neutral' }: StatTileProps) {
  return (
    <div className="cmd-panel cmd-panel-inner rounded px-3 py-2 flex flex-col gap-1">
      <span className="cmd-label truncate">{label}</span>
      <span
        className="text-[22px] leading-none font-mono tabular-nums font-semibold mt-0.5"
        style={{ color: TONE_NUMERAL[tone] }}
      >
        {value}
      </span>
    </div>
  );
}
